import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Send, RotateCcw, CalendarDays, AlertCircle, CheckCircle2, Briefcase, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import Lottie from 'lottie-react';
import loadingAnimation from '@/assets/animations/loading-blue.json';
import {
  useManagedDepartments,
  useCreateChangeRequest,
  type TemplateSearchResult,
} from '@/hooks/schedule-change-requests/useScheduleChangeRequests';
import { useDepartmentMonthSchedule } from '@/hooks/schedules/useDepartmentSchedule';
import { useTemplatesCalendarBatch } from '@/hooks/schedules/useTemplateCalendar';
import { type PreviewDay } from '@/utils/generateTemplatePreview';
import { SchedulePreviewGrid, type EmployeeRow } from '@/components/schedule-change-requests/SchedulePreviewGrid';
import { TemplateSelectDialog } from '@/components/schedule-change-requests/TemplateSelectDialog';
import { SmartScheduleDialog } from '@/components/optimization/SmartScheduleDialog';
import { APP_TIME_ZONE } from '@/utils/dateFormat';

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

interface ChangeEntry {
  templateId: string;
  templateName: string;
  recurrenceType: string;
  daysPattern: any;
}

function getNextMonth() {
  const now = new Date();
  const m = now.getMonth() + 2;
  if (m > 12) return { year: now.getFullYear() + 1, month: 1 };
  return { year: now.getFullYear(), month: m };
}

function generateMonthDates(year: number, month: number): Date[] {
  const daysInMonth = new Date(year, month, 0).getDate();
  const dates: Date[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    dates.push(new Date(year, month - 1, d));
  }
  return dates;
}

function formatYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function firstDayOfMonth(year: number, month: number): string {
  return formatYMD(new Date(year, month - 1, 1));
}

function lastDayOfMonth(year: number, month: number): string {
  return formatYMD(new Date(year, month, 0));
}

function tomorrow(): string {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return formatYMD(t);
}

// Минимально допустимая дата начала действия заявки.
// Если выбран будущий месяц — 1-е число этого месяца.
// Если выбран текущий или прошедший — завтра (заявка ретроактивно начинаться
// не должна, ровно сегодня тоже нельзя, чтобы успеть рассмотреть/применить).
function minValidFrom(year: number, month: number): string {
  const monthStart = firstDayOfMonth(year, month);
  const tmrw = tomorrow();
  return monthStart > tmrw ? monthStart : tmrw;
}

export const ScheduleManagement: React.FC = () => {
  const navigate = useNavigate();
  const nextMonth = useMemo(getNextMonth, []);
  const [selectedDeptId, setSelectedDeptId] = useState<string>('');
  const [year, setYear] = useState(nextMonth.year);
  const [month, setMonth] = useState(nextMonth.month);
  // Период действия заявки (с–по). Дата начала не может быть раньше завтра,
  // даже если переключиться на текущий месяц — заявку ретроактивно подавать нельзя.
  const [fromDate, setFromDate] = useState<string>(
    minValidFrom(nextMonth.year, nextMonth.month),
  );
  const [toDate, setToDate] = useState<string>(
    lastDayOfMonth(nextMonth.year, nextMonth.month),
  );
  const minFromDate = useMemo(() => minValidFrom(year, month), [year, month]);
  const [changes, setChanges] = useState<Map<string, ChangeEntry>>(new Map());
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);

  // Template select dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEmployeeId, setDialogEmployeeId] = useState<string>('');
  const [dialogEmployeeName, setDialogEmployeeName] = useState<string>('');

  // Smart Schedule dialog
  const [smartOpen, setSmartOpen] = useState(false);

  // Data
  const { data: departments, isLoading: deptsLoading } = useManagedDepartments();
  const createRequest = useCreateChangeRequest();

  const { data: scheduleData, isLoading: scheduleLoading } = useDepartmentMonthSchedule(
    selectedDeptId || null,
    year,
    month - 1, // 0-indexed
  );

  const dates = useMemo(() => generateMonthDates(year, month), [year, month]);

  // effectiveFromDate раньше использовался для подсветки "valid_from в прошлом —
  // применится с сегодня". Теперь пикер не пускает в прошлое (min=завтра),
  // так что оставляем undefined — превью календаря рисуется обычным образом.
  const effectiveFromDate: string | undefined = undefined;

  // Валидация диапазона
  const rangeError = useMemo(() => {
    if (!fromDate || !toDate) return null;
    if (fromDate < minFromDate) {
      return `Дата начала должна быть не раньше ${new Date(minFromDate).toLocaleDateString('ru-RU', { timeZone: APP_TIME_ZONE })}`;
    }
    if (fromDate > toDate) return 'Дата «по» должна быть не раньше даты «с»';
    return null;
  }, [fromDate, toDate, minFromDate]);

  const employees: EmployeeRow[] = useMemo(() => {
    if (!scheduleData?.employees) return [];
    return scheduleData.employees.map((emp) => {
      const change = changes.get(emp.employee_id);
      return {
        id: emp.employee_id,
        first_name: emp.first_name,
        last_name: emp.last_name,
        position: emp.position || null,
        schedule: emp.schedule,
        total_working_days: emp.total_working_days,
        total_days_off: emp.total_days_off,
        hasChange: !!change,
        changeLabel: change?.templateName,
      };
    });
  }, [scheduleData, changes]);

  // Extract unique positions from loaded employees
  const positions = useMemo(() => {
    if (!scheduleData?.employees) return [];
    const posMap = new Map<string, string>();
    for (const emp of scheduleData.employees) {
      if (emp.position?.id && emp.position?.name) {
        posMap.set(emp.position.id, emp.position.name);
      }
    }
    return Array.from(posMap, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [scheduleData]);

  // Filter employees by selected position
  const filteredEmployees = useMemo(() => {
    if (!selectedPositionId) return employees;
    return employees.filter((emp) => emp.position?.id === selectedPositionId);
  }, [employees, selectedPositionId]);

  const uniqueTemplateIds = useMemo(
    () => Array.from(new Set(Array.from(changes.values()).map((c) => c.templateId))),
    [changes],
  );
  const { calendars: templateCalendars } = useTemplatesCalendarBatch(
    uniqueTemplateIds,
    year,
    month,
  );

  const previewSchedules = useMemo(() => {
    const map = new Map<string, PreviewDay[]>();
    changes.forEach((entry, employeeId) => {
      const cal = templateCalendars.get(entry.templateId);
      if (!cal) return;
      const preview: PreviewDay[] = cal.map((c) => {
        const shiftStart = c.shift_start ? String(c.shift_start) : null;
        const shiftEnd = c.shift_end ? String(c.shift_end) : null;
        return {
          date: String(c.date).slice(0, 10),
          is_working_day: !!c.is_working_day,
          shift_start: shiftStart,
          shift_end: shiftEnd,
          schedule_type: c.schedule_type || (c.is_working_day ? 'working' : 'day_off'),
        };
      });
      map.set(employeeId, preview);
    });
    return map;
  }, [changes, templateCalendars]);

  // Handlers
  // Переключение месяца ⇒ сбрасываем пикеры дат на 1-е и последнее число
  // выбранного месяца, чтобы менеджер видел консистентный диапазон.
  // Если он хочет нестандартный период — отредактирует пикеры.
  const handlePrevMonth = useCallback(() => {
    setMonth((prev) => {
      let newYear = year;
      let newMonth = prev;
      if (prev === 1) { newYear = year - 1; newMonth = 12; }
      else { newMonth = prev - 1; }
      setYear(newYear);
      setFromDate(minValidFrom(newYear, newMonth));
      setToDate(lastDayOfMonth(newYear, newMonth));
      return newMonth;
    });
    setChanges(new Map());
    setSubmitSuccess(false);
    setSubmitError('');
  }, [year]);

  const handleNextMonth = useCallback(() => {
    setMonth((prev) => {
      let newYear = year;
      let newMonth = prev;
      if (prev === 12) { newYear = year + 1; newMonth = 1; }
      else { newMonth = prev + 1; }
      setYear(newYear);
      setFromDate(minValidFrom(newYear, newMonth));
      setToDate(lastDayOfMonth(newYear, newMonth));
      return newMonth;
    });
    setChanges(new Map());
    setSubmitSuccess(false);
    setSubmitError('');
  }, [year]);

  const handleDepartmentChange = useCallback((value: string) => {
    setSelectedDeptId(value);
    setSelectedPositionId(null);
    setChanges(new Map());
    setSubmitSuccess(false);
    setSubmitError('');
  }, []);

  const handleSelectTemplate = useCallback((employeeId: string) => {
    const emp = employees.find((e) => e.id === employeeId);
    if (!emp) return;
    setDialogEmployeeId(employeeId);
    setDialogEmployeeName(`${emp.last_name} ${emp.first_name}`);
    setDialogOpen(true);
  }, [employees]);

  const handleTemplateSelected = useCallback((template: TemplateSearchResult) => {
    setChanges((prev) => {
      const next = new Map(prev);
      next.set(dialogEmployeeId, {
        templateId: template.id,
        templateName: template.name,
        recurrenceType: template.recurrence_type,
        daysPattern: template.days_pattern,
      });
      return next;
    });
    setSubmitSuccess(false);
    setSubmitError('');
  }, [dialogEmployeeId]);

  const handleRemoveChange = useCallback((employeeId: string) => {
    setChanges((prev) => {
      const next = new Map(prev);
      next.delete(employeeId);
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    setChanges(new Map());
    setSubmitSuccess(false);
    setSubmitError('');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (changes.size === 0 || !selectedDeptId) return;
    if (rangeError) {
      setSubmitError(rangeError);
      return;
    }
    setSubmitError('');
    setSubmitSuccess(false);

    const items = Array.from(changes.entries()).map(([employeeId, entry]) => ({
      employee_id: employeeId,
      new_template_id: entry.templateId,
    }));

    try {
      await createRequest.mutateAsync({
        department_id: selectedDeptId,
        valid_from: fromDate,
        valid_until: toDate,
        items,
      });
      navigate('/schedule-change-requests', { state: { success: true } });
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Ошибка при отправке запроса');
    }
  }, [changes, selectedDeptId, fromDate, toDate, rangeError, createRequest, navigate]);

  // Loading / empty states
  if (deptsLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!departments || departments.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Управление графиками</h1>
        <Card>
          <CardContent className="p-8 text-center">
            <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-lg">У вас нет подчинённых отделов</p>
            <p className="text-muted-foreground text-sm mt-1">
              Обратитесь к администратору для назначения роли управляющего отделом.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Управление графиками</h1>
        <div className="flex items-center gap-3">
          {changes.size > 0 && (
            <Badge variant="default" className="text-sm">
              Изменено: {changes.size}
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSmartOpen(true)}
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Сгенерировать AI
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="w-64">
          <Select value={selectedDeptId || ''} onValueChange={handleDepartmentChange}>
            <SelectTrigger>
              <SelectValue placeholder="Выберите отдел" />
            </SelectTrigger>
            <SelectContent>
              {departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-52">
          <Select
            value={selectedPositionId || 'all'}
            onValueChange={(value) => setSelectedPositionId(value === 'all' ? null : value)}
            disabled={!scheduleData}
          >
            <SelectTrigger>
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-muted-foreground" />
                <SelectValue placeholder="Все должности" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все должности</SelectItem>
              {positions.map((pos) => (
                <SelectItem key={pos.id} value={pos.id}>
                  {pos.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handlePrevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-[120px] text-center">
            {MONTH_NAMES[month - 1]} {year}
          </span>
          <Button variant="outline" size="icon" onClick={handleNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-end gap-2 pl-2 border-l">
          <div>
            <Label htmlFor="period-from" className="text-xs text-muted-foreground">
              Действует с
            </Label>
            <Input
              id="period-from"
              type="date"
              value={fromDate}
              min={minFromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-40 h-9"
            />
          </div>
          <div>
            <Label htmlFor="period-to" className="text-xs text-muted-foreground">
              По (включительно)
            </Label>
            <Input
              id="period-to"
              type="date"
              value={toDate}
              min={fromDate || minFromDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-40 h-9"
            />
          </div>
        </div>
      </div>

      {/* Range validation */}
      {rangeError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{rangeError}</AlertDescription>
        </Alert>
      )}

      {/* Calendar grid */}
      {!selectedDeptId ? (
        <Card>
          <CardContent className="flex items-center justify-center py-16">
            <div className="text-center text-muted-foreground">
              <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Выберите отдел для просмотра графика</p>
            </div>
          </CardContent>
        </Card>
      ) : scheduleLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-8">
            <Lottie animationData={loadingAnimation} loop className="w-48 h-48" />
          </CardContent>
        </Card>
      ) : filteredEmployees.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">В отделе нет сотрудников.</p>
          </CardContent>
        </Card>
      ) : (
        <SchedulePreviewGrid
          employees={filteredEmployees}
          dates={dates}
          previewSchedules={previewSchedules}
          onSelectTemplate={handleSelectTemplate}
          onRemoveChange={handleRemoveChange}
          showDiff={changes.size > 0}
          effectiveFromDate={effectiveFromDate}
        />
      )}

      {/* Success / Error */}
      {submitSuccess && (
        <Alert className="border-green-300 bg-green-50">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <AlertTitle className="text-green-800">Заявка отправлена</AlertTitle>
          <AlertDescription className="text-green-700">
            Заявка на изменение графиков успешно отправлена на рассмотрение HR.
            Вы можете отслеживать статус в разделе «Заявки на графики».
          </AlertDescription>
        </Alert>
      )}
      {submitError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      {/* Floating action bar — fixed at bottom when there are changes */}
      {changes.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t shadow-lg px-6 py-3">
          <div className="flex items-center justify-between max-w-[1600px] mx-auto">
            <span className="text-sm font-medium">
              Изменено: {changes.size}{' '}
              {changes.size === 1 ? 'сотрудник' : changes.size < 5 ? 'сотрудника' : 'сотрудников'}
            </span>
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={handleReset}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Сбросить
              </Button>
              <Button size="sm" onClick={handleSubmit} disabled={createRequest.isPending}>
                <Send className="h-4 w-4 mr-2" />
                {createRequest.isPending ? 'Отправка...' : 'Отправить на утверждение'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Template select modal */}
      <TemplateSelectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        employeeName={dialogEmployeeName}
        onSelect={handleTemplateSelected}
      />

      {/* Smart Schedule modal */}
      <SmartScheduleDialog open={smartOpen} onOpenChange={setSmartOpen} />
    </div>
  );
};

export default ScheduleManagement;
